(() => {
  'use strict';

  const MAX_PETALS = 4;
  const PALETTES = {
    blue: { surface: '#76bde0', tree: '#eaf5fb', petal: '#eaf5fb' },
    ivory: { surface: '#fbf7ef', tree: '#76bde0', petal: '#76bde0' },
    tint: { surface: '#eaf5fb', tree: '#2a6e92', petal: '#2a6e92' },
    wash: { surface: '#f4fafd', tree: '#76bde0', petal: '#76bde0' }
  };
  const SIZES = {
    small: [9, 12, 15, 18],
    balanced: [11, 14, 18, 22],
    larger: [13, 17, 21, 25]
  };
  const FALL_RATES = {
    very_gentle: [0.06, 0.10, 0.14, 0.18],
    gentle: [0.10, 0.15, 0.21, 0.27],
    moderate: [0.14, 0.20, 0.26, 0.32]
  };
  const DRIFT = {
    vertical: [0, 0, 0, 0],
    subtle_diagonal: [0.15, -0.18, 0.16, -0.15],
    gentle_diagonal: [0.25, -0.28, 0.30, -0.25],
    stronger_diagonal: [0.30, -0.30, 0.30, -0.30]
  };
  const START_Y = [0.21, 0.63, 0.38, 0.78];
  const TREE_X = [0.14, 0.72, 0.43, 0.86];
  const SIZE_ORDERS = [[2, 0, 3, 1], [1, 3, 0, 2], [3, 1, 2, 0], [2, 1, 0, 3]];
  const ACTIVATION_LEAD = [0.72, 1.06, 0.84, 1.20];
  const STATIC_Y = [0.24, 0.57, 0.36, 0.70];
  const FOOTER_START_Y = [0.08, 0.32, 0.18, 0.46];
  const ROTATIONS = [-18, 34, 68, -42];

  const parseHex = (value) => {
    const number = Number.parseInt(value.slice(1), 16);
    return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
  };
  const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));

  function resetModules(modules) {
    modules.forEach((item) => {
      item.element.removeAttribute('data-sakura-palette');
      item.element.removeAttribute('data-sakura-tree');
      item.element.style.removeProperty('--sakura-module-surface');
      item.element.style.removeProperty('--sakura-tree-color');
      item.element.style.removeProperty('--sakura-tree-opacity');
    });
  }

  function cleanupActiveRelay() {
    if (typeof window.__sakuraBotanicalRelayCleanup === 'function') {
      window.__sakuraBotanicalRelayCleanup();
    }
    window.__sakuraBotanicalRelayCleanup = null;
  }

  function petalCount(config, mobile) {
    if (mobile) {
      if (config.mobileDensity === 'off') return 0;
      return config.mobileDensity === 'minimal' ? 2 : 3;
    }
    return config.petalLayout === 'sparse' ? 3 : MAX_PETALS;
  }

  function treeBounds(item, mainRect, mainTop, pageWidth) {
    const tree = item.element.querySelector('[class*="sakura-decoration--"]');
    const rect = tree ? tree.getBoundingClientRect() : item.element.getBoundingClientRect();
    const left = clamp(rect.left - mainRect.left, 0, pageWidth);
    const right = clamp(rect.right - mainRect.left, left, pageWidth);
    const top = rect.top + scrollY - mainTop;
    const bottom = rect.bottom + scrollY - mainTop;
    return { left, right, top, bottom, width: Math.max(1, right - left), height: Math.max(1, bottom - top) };
  }

  function initializeRelay(relay) {
    cleanupActiveRelay();

    const layer = relay.querySelector('.sakura-botanical-relay__layer');
    const configNode = relay.querySelector('[data-sakura-relay-config]');
    const main = document.querySelector('#mainContent');
    if (!layer || !configNode || !main) return;

    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (_error) {
      layer.textContent = '';
      relay.hidden = true;
      return;
    }

    const configuredModules = config.modules
      .map((item) => ({ ...item, element: document.querySelector(item.selector) }))
      .filter((item) => item.element && PALETTES[item.palette]);
    const footerModule = configuredModules.find((item) => item.key === 'footer') || null;
    const modules = configuredModules.filter((item) => item.key !== 'footer');

    layer.textContent = '';
    if (!config.enabled || modules.length < 2) {
      resetModules(configuredModules);
      relay.hidden = true;
      window.__sakuraBotanicalRelayCleanup = () => resetModules(configuredModules);
      return;
    }

    configuredModules.forEach((item) => {
      const palette = PALETTES[item.palette];
      item.element.dataset.sakuraPalette = item.palette;
      item.element.dataset.sakuraTree = item.tree;
      item.element.style.setProperty('--sakura-module-surface', palette.surface);
      item.element.style.setProperty('--sakura-tree-color', palette.tree);
      item.element.style.setProperty('--sakura-tree-opacity', String(clamp(Number(item.treeOpacity) / 100, 0.05, 0.6)));
    });
    relay.hidden = false;

    const mediaMobile = matchMedia('(max-width: 767px)');
    const mediaReduced = matchMedia('(prefers-reduced-motion: reduce)');
    const particles = [];
    const footerParticles = [];
    let geometry = [];
    let footerGeometry = [];
    let footerLayer = null;

    const buildParticles = () => {
      layer.textContent = '';
      particles.length = 0;
      const count = petalCount(config, mediaMobile.matches);
      const sizes = SIZES[config.petalSize] || SIZES.balanced;
      for (let transition = 0; transition < modules.length - 1; transition += 1) {
        const sizeOrder = SIZE_ORDERS[transition % SIZE_ORDERS.length];
        for (let index = 0; index < count; index += 1) {
          const node = document.createElement('span');
          node.className = 'sakura-botanical-relay__petal';
          node.dataset.transition = String(transition);
          node.style.setProperty('--sakura-petal-size', `${sizes[sizeOrder[index]]}px`);
          node.style.setProperty('--sakura-petal-rotation', `${ROTATIONS[(index + transition) % ROTATIONS.length]}deg`);
          layer.append(node);
          particles.push({ node, transition, index });
        }
      }
    };

    function buildFooterParticles() {
      if (footerLayer) footerLayer.remove();
      footerLayer = null;
      footerParticles.length = 0;
      if (!footerModule) return;

      footerLayer = document.createElement('div');
      footerLayer.className = 'sakura-footer-petal-layer';
      footerLayer.setAttribute('aria-hidden', 'true');
      footerModule.element.append(footerLayer);

      const count = petalCount(config, mediaMobile.matches);
      const sizes = SIZES[config.petalSize] || SIZES.balanced;
      const sizeOrder = SIZE_ORDERS[(modules.length - 1) % SIZE_ORDERS.length];
      for (let index = 0; index < count; index += 1) {
        const node = document.createElement('span');
        node.className = 'sakura-botanical-relay__petal';
        node.dataset.footerPetal = String(index);
        node.style.setProperty('--sakura-petal-size', `${sizes[sizeOrder[index]]}px`);
        node.style.setProperty('--sakura-petal-rotation', `${ROTATIONS[(index + modules.length) % ROTATIONS.length]}deg`);
        footerLayer.append(node);
        footerParticles.push({ node, index });
      }
    }

    const measure = () => {
      const mainRect = main.getBoundingClientRect();
      const mainTop = mainRect.top + scrollY;
      const width = main.clientWidth;
      const sizes = SIZES[config.petalSize] || SIZES.balanced;
      const driftSet = DRIFT[config.movement] || DRIFT.gentle_diagonal;
      const fallSet = FALL_RATES[config.pace] || FALL_RATES.gentle;
      geometry = [];

      for (let transition = 0; transition < modules.length - 1; transition += 1) {
        const sourceTree = treeBounds(modules[transition], mainRect, mainTop, width);
        const targetRect = modules[transition + 1].element.getBoundingClientRect();
        const targetTop = targetRect.top + scrollY - mainTop;
        const targetBottom = targetRect.bottom + scrollY - mainTop;
        const targetHeight = Math.max(1, targetBottom - targetTop);
        const overlapY = clamp(targetHeight * 0.12, 56, 120);
        const fadeStartY = targetBottom - clamp(targetHeight * 0.18, 84, 210);
        const fadeEndY = targetBottom;
        const sizeOrder = SIZE_ORDERS[transition % SIZE_ORDERS.length];
        const stage = [];

        for (let index = 0; index < MAX_PETALS; index += 1) {
          const size = sizes[sizeOrder[index]];
          const startY = transition === 0
            ? sourceTree.top + sourceTree.height * START_Y[index]
            : targetTop - overlapY * ACTIVATION_LEAD[index];
          const destY = fadeEndY;
          const travelY = Math.max(1, destY - startY);
          const useMobileOpeningEdge = mediaMobile.matches && transition === 0;
          const mobileOpeningInset = index === 2 ? size + 18 : 4;
          const mobileOpeningX = index % 2 === 0
            ? sourceTree.left + mobileOpeningInset
            : sourceTree.right - size - 4;
          const treeIndex = (index + transition) % TREE_X.length;
          const safeLeft = 12;
          const safeRight = Math.max(safeLeft, width - size - 12);
          const anchorX = useMobileOpeningEdge
            ? clamp(mobileOpeningX, 4, Math.max(4, width - size - 4))
            : clamp(sourceTree.left + sourceTree.width * TREE_X[treeIndex], 12, Math.max(12, width - size - 12));
          const horizontalSpan = Math.max(1, safeRight - safeLeft);
          const driftX = horizontalSpan * driftSet[index];
          const startX = clamp(
            anchorX,
            safeLeft + Math.max(0, -driftX),
            safeRight - Math.max(0, driftX)
          );
          const requestedDestX = startX + driftX;
          const destX = clamp(requestedDestX, safeLeft, safeRight);
          const activation = transition === 0
            ? 0
            : Math.max(0, mainTop + targetTop - innerHeight);
          const staticY = targetTop + targetHeight * STATIC_Y[index];
          const staticProgress = clamp((staticY - startY) / travelY);
          const staticX = startX + (destX - startX) * staticProgress;
          stage.push({
            startY,
            destY,
            travelY,
            startX,
            destX,
            activation,
            fall: fallSet[index],
            handoffY: targetTop,
            fadeStartY,
            fadeEndY,
            staticX,
            staticY
          });
        }
        geometry.push(stage);
      }
    };

    function measureFooter() {
      footerGeometry = [];
      if (!footerModule || !footerLayer) return;

      const rect = footerModule.element.getBoundingClientRect();
      const footerTop = rect.top + scrollY;
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);
      const sizes = SIZES[config.petalSize] || SIZES.balanced;
      const driftSet = DRIFT[config.movement] || DRIFT.gentle_diagonal;
      const fallSet = FALL_RATES[config.pace] || FALL_RATES.gentle;
      const sizeOrder = SIZE_ORDERS[(modules.length - 1) % SIZE_ORDERS.length];

      for (let index = 0; index < MAX_PETALS; index += 1) {
        const size = sizes[sizeOrder[index]];
        const safeLeft = 12;
        const safeRight = Math.max(safeLeft, width - size - 12);
        const horizontalSpan = Math.max(1, safeRight - safeLeft);
        const driftX = horizontalSpan * driftSet[index];
        const anchorX = clamp(width * TREE_X[(index + modules.length) % TREE_X.length], safeLeft, safeRight);
        const startX = clamp(
          anchorX,
          safeLeft + Math.max(0, -driftX),
          safeRight - Math.max(0, driftX)
        );
        const destX = clamp(startX + driftX, safeLeft, safeRight);
        const startY = height * FOOTER_START_Y[index];
        const fadeStartY = height * 0.76;
        const fadeEndY = height;
        const travelY = Math.max(1, fadeEndY - startY);
        const staticY = height * STATIC_Y[index];
        const staticProgress = clamp((staticY - startY) / travelY);
        footerGeometry.push({
          startX,
          destX,
          startY,
          fadeStartY,
          fadeEndY,
          travelY,
          activation: Math.max(0, footerTop - innerHeight * 0.88),
          fall: fallSet[index],
          staticX: startX + (destX - startX) * staticProgress,
          staticY
        });
      }
    }

    const paint = (particle, x, y, color, opacity) => {
      particle.node.style.setProperty('--sakura-petal-dx', `${x.toFixed(2)}px`);
      particle.node.style.setProperty('--sakura-petal-y', `${y.toFixed(2)}px`);
      particle.node.style.setProperty('--sakura-petal-color', `rgb(${color.join(',')})`);
      particle.node.style.setProperty('--sakura-petal-opacity', opacity.toFixed(3));
    };

    const render = () => {
      const localScroll = Math.max(0, scrollY);
      const fullerOpacity = config.petalLayout === 'fuller' ? 0.08 : 0;
      particles.forEach((particle) => {
        const stage = geometry[particle.transition] && geometry[particle.transition][particle.index];
        if (!stage) return;
        const source = parseHex(PALETTES[modules[particle.transition].palette].petal);
        const destination = parseHex(PALETTES[modules[particle.transition + 1].palette].petal);

        if (mediaReduced.matches) {
          paint(particle, stage.staticX, stage.staticY, destination, 0.30 + fullerOpacity);
          return;
        }
        if (localScroll < stage.activation) {
          paint(particle, stage.startX, stage.startY, source, 0);
          return;
        }

        const elapsed = localScroll - stage.activation;
        const rawTravel = elapsed * (1 + stage.fall);
        const travelled = clamp(rawTravel, 0, stage.travelY);
        const progress = travelled / stage.travelY;
        const x = stage.startX + (stage.destX - stage.startX) * progress;
        const y = stage.startY + travelled;
        const color = y < stage.handoffY ? source : destination;
        const introOpacity = particle.transition === 0 ? 1 : clamp(progress / 0.08);
        const fadeProgress = clamp((y - stage.fadeStartY) / Math.max(1, stage.fadeEndY - stage.fadeStartY));
        const opacity = (0.34 + fullerOpacity + introOpacity * 0.12) * introOpacity * (1 - fadeProgress);
        paint(particle, x, y, color, opacity);
      });
    };

    function renderFooter() {
      if (!footerModule || !footerLayer) return;
      const localScroll = Math.max(0, scrollY);
      const color = parseHex(PALETTES[footerModule.palette].petal);
      const fullerOpacity = config.petalLayout === 'fuller' ? 0.06 : 0;

      footerParticles.forEach((particle) => {
        const stage = footerGeometry[particle.index];
        if (!stage) return;
        if (mediaReduced.matches) {
          paint(particle, stage.staticX, stage.staticY, color, 0.24 + fullerOpacity);
          return;
        }
        if (localScroll < stage.activation) {
          paint(particle, stage.startX, stage.startY, color, 0);
          return;
        }

        const elapsed = localScroll - stage.activation;
        const travelled = clamp(elapsed * (1 + stage.fall), 0, stage.travelY);
        const progress = travelled / stage.travelY;
        const x = stage.startX + (stage.destX - stage.startX) * progress;
        const y = stage.startY + travelled;
        const introOpacity = clamp(progress / 0.08);
        const fadeProgress = clamp((y - stage.fadeStartY) / Math.max(1, stage.fadeEndY - stage.fadeStartY));
        const opacity = (0.30 + fullerOpacity + introOpacity * 0.10) * introOpacity * (1 - fadeProgress);
        paint(particle, x, y, color, opacity);
      });
    }

    const rebuild = () => {
      buildParticles();
      buildFooterParticles();
      measure();
      measureFooter();
      render();
      renderFooter();
    };
    const onScroll = () => {
      render();
      renderFooter();
    };
    const onMediaChange = () => rebuild();
    const resizeObserver = new ResizeObserver(() => {
      measure();
      measureFooter();
      render();
      renderFooter();
    });

    addEventListener('scroll', onScroll, { passive: true });
    mediaMobile.addEventListener('change', onMediaChange);
    mediaReduced.addEventListener('change', onMediaChange);
    modules.forEach((item) => resizeObserver.observe(item.element));
    if (footerModule) resizeObserver.observe(footerModule.element);
    rebuild();

    window.__sakuraBotanicalRelayCleanup = () => {
      removeEventListener('scroll', onScroll);
      mediaMobile.removeEventListener('change', onMediaChange);
      mediaReduced.removeEventListener('change', onMediaChange);
      resizeObserver.disconnect();
      layer.textContent = '';
      if (footerLayer) footerLayer.remove();
      resetModules(configuredModules);
    };
  }

  const initializeAll = () => {
    const relay = document.querySelector('[data-sakura-botanical-relay]');
    if (relay) initializeRelay(relay);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    const target = event.target;
    if (target && (target.matches('[data-sakura-botanical-relay]') || target.querySelector('[data-sakura-botanical-relay]'))) {
      cleanupActiveRelay();
    }
  });
})();
